import { Box } from '@mui/material'
import Container from '@mui/material/Container'
import CssBaseline from '@mui/material/CssBaseline'
import type { Localization } from '@mui/material/locale'
import { elGR, enUS, fiFI, nlNL, svSE } from '@mui/material/locale'
import { ThemeProvider } from '@mui/material/styles'
import {
  elGR as dataGridElGR,
  enUS as dataGridEnUS,
  fiFI as dataGridFiFI,
  nlNL as dataGridNlNL,
  svSE as dataGridSvSE,
} from '@mui/x-data-grid/locales'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { Outlet } from 'react-router-dom'

import WelcomeDialog from '@/components/feedback/WelcomeDialog'
import AppTopBar from '@/components/layouts/AppTopBar'
import NavBar from '@/components/navigation/NavBar'
import {
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_TRANSITION_DURATION_MS,
  SIDEBAR_TRANSITION_EASING,
  SIDEBAR_WIDTH,
} from '@/constants/layout'
import { AlertProvider } from '@/contexts/AlertContext'
import { AlertRouteWatcher } from '@/contexts/AlertRouteWatcher'
import { SidebarProvider } from '@/contexts/SidebarContext'
import { useAuth } from '@/features/authentication/contexts/AuthContext'
import { useMuiTheme } from '@/features/whitelabel/hooks/useMuiTheme'

const SIDEBAR_STORAGE_KEY = 'flexportal-sidebar-collapsed'

type ShellContextType = {
  isFullWidth: boolean
  setIsFullWidth: (value: boolean) => void
}

const ShellContext = createContext<ShellContextType | undefined>(undefined)

export function useShellFullWidth() {
  const context = useContext(ShellContext)
  if (!context) {
    throw new Error('useShellFullWidth must be used within Shell')
  }
  return context
}

type DataGridLocalization = typeof dataGridEnUS

const DATA_GRID_LOCALES: { [key: string]: DataGridLocalization } = {
  en: dataGridEnUS,
  nl: dataGridNlNL,
  fi: dataGridFiFI,
  sv: dataGridSvSE,
  el: dataGridElGR,
}

const MUI_LOCALES: { [key: string]: Localization } = {
  en: enUS,
  nl: nlNL,
  fi: fiFI,
  sv: svSE,
  el: elGR,
}

function Shell() {
  const { loggedInUser } = useAuth()
  const [isFullWidth, setIsFullWidth] = useState(false)
  const theme = useMuiTheme(
    loggedInUser?.uiLanguage ? DATA_GRID_LOCALES[loggedInUser.uiLanguage] : DATA_GRID_LOCALES.en,
    loggedInUser?.uiLanguage ? MUI_LOCALES[loggedInUser.uiLanguage] : MUI_LOCALES.en,
  )

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true'
  })

  const toggleSidebar = useCallback(() => {
    setIsSidebarCollapsed((prev) => {
      const newValue = !prev
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(newValue))
      return newValue
    })
  }, [])

  const expandSidebar = useCallback(() => {
    setIsSidebarCollapsed(false)
    localStorage.setItem(SIDEBAR_STORAGE_KEY, 'false')
  }, [])

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === '[' || event.key === ']') {
        const target = event.target as HTMLElement
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
          return
        }
        toggleSidebar()
      }
    }
    globalThis.addEventListener('keydown', handleKeyDown)
    return () => globalThis.removeEventListener('keydown', handleKeyDown)
  }, [toggleSidebar])

  const drawerWidth = isSidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH

  return (
    <ThemeProvider theme={theme}>
      <AlertProvider>
        <AlertRouteWatcher />
        <ShellContext.Provider value={{ isFullWidth, setIsFullWidth }}>
          <SidebarProvider isCollapsed={isSidebarCollapsed} onExpand={expandSidebar} onToggle={toggleSidebar}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
              <CssBaseline />
              <AppTopBar />
              <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
                <NavBar
                  drawerWidth={drawerWidth}
                  isCollapsed={isSidebarCollapsed}
                  sidebarEasing={SIDEBAR_TRANSITION_EASING}
                  sidebarTransitionDuration={SIDEBAR_TRANSITION_DURATION_MS}
                />
                <Box
                  component="main"
                  sx={{
                    flexGrow: 1,
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Container
                    maxWidth={isFullWidth ? false : 'xl'}
                    sx={{ mt: 2, flex: 1, display: 'flex', flexDirection: 'column' }}
                  >
                    <Outlet />
                  </Container>
                </Box>
              </Box>
              <WelcomeDialog />
            </Box>
          </SidebarProvider>
        </ShellContext.Provider>
      </AlertProvider>
    </ThemeProvider>
  )
}

export default Shell
